import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui/button";
import { toast } from "@patient-watch/ui/sonner";
import { User } from "lucide-react";
import { LeadGenDetailShell } from "@/features/lead-gen/components/LeadGenDetailShell";
import { TouchEditorCard } from "@/features/lead-gen/components/TouchEditorCard";
import { TouchKindIcon } from "@/features/lead-gen/components/TouchKindIcon";
import { useLeadGen } from "@/features/lead-gen/LeadGenContext";
import { formatLeadDate } from "@/features/lead-gen/schedule";
import { applyTouchChange, leadColleagues, touchKindLabel, type Touch } from "@/features/lead-gen/types";
import { useNav } from "@/utils/nav";

export const Route = createFileRoute("/_app/sales/leads/touches/$touchId")({
    component: TouchDetailPage,
});

function TouchDetailPage() {
    const { touchId } = Route.useParams();
    const { leads, setLeads, companies, templates } = useLeadGen();
    const navigate = useNavigate();

    const lead = leads.find((row) => row.touches.some((touch) => touch.touch_id === touchId));
    const touch = lead?.touches.find((item) => item.touch_id === touchId);

    useNav([
        { label: "Sales", route: { to: "/sales/leads" } },
        { label: "Leads", route: { to: "/sales/leads" } },
        { label: "Touches", route: { to: "/sales/leads/touches" } },
        {
            label: touch ? touchKindLabel(touch.kind) : "Touch",
            route: { to: "/sales/leads/touches/$touchId", params: { touchId } },
        },
    ]);

    if (!lead || !touch) {
        return (
            <LeadGenDetailShell
                backTo="/sales/leads/touches"
                backLabel="All touches"
                summary={<p className="text-slate-500 text-sm">This touch may have been deleted.</p>}
            >
                <p className="bg-white/70 p-6 border border-slate-200 rounded-lg text-slate-500 text-sm text-center">
                    Touch not found.
                </p>
            </LeadGenDetailShell>
        );
    }

    const updateTouch = (next: Touch) => {
        setLeads((current) => current.map((row) => (row.lead_id === lead.lead_id ? applyTouchChange(row, next) : row)));
    };

    const removeTouch = () => {
        setLeads((current) =>
            current.map((row) =>
                row.lead_id === lead.lead_id
                    ? { ...row, touches: row.touches.filter((item) => item.touch_id !== touchId) }
                    : row
            )
        );
        toast.success("Touch deleted");
        void navigate({ to: "/sales/leads/touches" });
    };

    return (
        <LeadGenDetailShell
            backTo="/sales/leads/touches"
            backLabel="All touches"
            summary={
                <div className="flex items-center gap-2 min-w-0">
                    <TouchKindIcon kind={touch.kind} />
                    <span className="font-medium text-slate-800 text-lg">{touchKindLabel(touch.kind)}</span>
                    <span className="text-slate-500 text-sm truncate">
                        {[lead.name || "Untitled", lead.company, formatLeadDate(touch.date)]
                            .filter(Boolean)
                            .join(" · ")}
                    </span>
                </div>
            }
            actions={
                <Button type="button" variant="outline" size="sm" className="min-h-0" asChild>
                    <Link to="/sales/leads/$leadId" params={{ leadId: lead.lead_id }}>
                        <User className="w-3.5 h-3.5" />
                        Open lead
                    </Link>
                </Button>
            }
        >
            <TouchEditorCard
                touch={touch}
                aiContext={{
                    person: {
                        name: lead.name,
                        role: lead.role,
                        email: lead.email,
                        linkedin_url: lead.linkedin_url,
                    },
                    company: lead.company,
                    companyContexts: companies.find(
                        (item) => item.name.localeCompare(lead.company, undefined, { sensitivity: "base" }) === 0
                    )?.contexts,
                    team: lead.team,
                    otherTouches: lead.touches,
                    colleagues: leadColleagues(leads, lead),
                    templates,
                }}
                onChange={updateTouch}
                onDelete={removeTouch}
            />

            <section className="space-y-2">
                <h2 className="font-medium text-slate-800 text-sm">Rest of the sequence</h2>
                <div className="bg-white/70 border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {lead.touches
                        .filter((item) => item.touch_id !== touchId)
                        .map((item) => (
                            <Link
                                key={item.touch_id}
                                to="/sales/leads/touches/$touchId"
                                params={{ touchId: item.touch_id }}
                                className="flex items-center gap-2 hover:bg-slate-50 p-2.5"
                            >
                                <TouchKindIcon kind={item.kind} />
                                <span className="text-slate-700 text-sm">{touchKindLabel(item.kind)}</span>
                                <span className="text-2xs text-slate-400">{formatLeadDate(item.date)}</span>
                                <span className="flex-1 text-slate-500 text-sm truncate">
                                    {item.message.trim() || "No message"}
                                </span>
                            </Link>
                        ))}
                    {lead.touches.length <= 1 ? (
                        <p className="p-2.5 text-slate-500 text-sm">No other touches for this lead.</p>
                    ) : null}
                </div>
            </section>
        </LeadGenDetailShell>
    );
}
